import { createFinishStepContent } from '../FinishStepContent';
import { closeSidebarPanel, openPersonalizationSubmenu } from '../sidebarHelpers';

const createRecommender = (baseUrl, navigate, recommender, user) => {
  return {
    id: 'createRecommender',
    title: 'Create Recommender',
    steps: [
      {
        selector: '[data-tour="webPersonalizationLink"]',
        content: <p>Click on the "Personalization" menu item in the navigation.</p>,
      },
      {
        actionBefore: () => openPersonalizationSubmenu('[data-tour="recommenderLink"]'),
        selector: '[data-tour="recommenderLink"]',
        content: (
          <>
            <p>
              What we call a Product Block are visual elements that use AI to deliver the most relevant products to each
              customer in the form of a product carousel.
            </p>
            <p>Click on "Product Blocks".</p>
          </>
        ),
      },
      {
        actionBefore: () => {
          closeSidebarPanel();
          navigate(`${baseUrl}/pages?page=${recommender.pageId}&type=recommenders`, true);
        },
        selector: '[data-tour="pagesSelect"]',
        content: (
          <>
            <p>Use this dropdown to select between different pages you can add Releva elements to.</p>
            <p>
              You can have multiple product blocks on each page each with a different algorithm and displaying different
              products or addressing a different group of users.
            </p>
          </>
        ),
      },
      {
        actionBefore: () => navigate(`${baseUrl}/pages?page=${recommender.pageId}&type=recommenders`, true),
        selector: '[data-tour="recommenderForYou"]',
        content: (
          <>
            <p>
              Below the name of the product block, you can see more information about the segment and mode it uses and
              performance statistics.
            </p>
            <p>Click on the name of each product block to see details about it and be able to make changes.</p>
          </>
        ),
      },
      {
        actionBefore: () => navigate(`${baseUrl}/pages?page=${recommender.pageId}&type=recommenders`, true),
        selector: '[data-tour="createRecommenderBtn"]',
        content: <p>Click New Product Block.</p>,
      },
      {
        actionBefore: () => navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true),
        selector: '[data-tour="recommenderName"]',
        content: (
          <p>
            This is the internal name of the element which must be unique and will not be visible to users on the
            website.
          </p>
        ),
        stepInteraction: true,
      },
      {
        actionBefore: () => navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true),
        selector: '[data-tour="recommenderTitle"]',
        content: (
          <>
            <p>This is the heading of the element visible on the website.</p>
            <p>You can customize this however you like, this is what your users will see.</p>
          </>
        ),
        stepInteraction: true,
      },
      {
        actionBefore: () => navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true),
        selector: '[data-tour="recommenderGeneralMode"]',
        content: (
          <p>
            This setting refers to whether there might be a repetition of some products already on the page or the
            presented products would be new suggestions.
          </p>
        ),
      },
      {
        actionBefore: () => navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true),
        selector: '[data-tour="recommenderProductsSlider"]',
        content: <p>You can choose the number of products to display on each carousel.</p>,
        stepInteraction: true,
      },
      {
        actionBefore: () => navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true),
        selector: '[data-tour="recommenderProductFeed"]',
        content: (
          <>
            <p>This is where the magic happens.</p>
            <p>The product feed will create the general pool of products personalized for each visitor.</p>
            <p>Click on each product feed to see its configuration. You can create your own product feeds later on.</p>
          </>
        ),
        stepInteraction: true,
      },
      {
        actionBefore: () => navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true),
        selector: '[data-tour="recommenderAudienceTab"]',
        content: <p>Click on the Audience tab to see what users will see this product block.</p>,
      },
      {
        actionBefore: () => {
          document.querySelector('[data-tour="recommenderAudienceTab"]')?.click();
          navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true);
        },
        selector: '[data-tour="segmentField"]',
        content: (
          <>
            <p>Here you can choose a group of users who will see this particular element.</p>
            <p>You can choose as wide a group as you want.</p>
            <p>
              For some product blocks that display discounted products, you might want to show them only to anonymous
              users in order to tempt them to make the first purchase, for example.
            </p>
            <p>
              To the right, you will get a real-time estimate of how many users fall in this segment and the element
              will be displayed.
            </p>
          </>
        ),
        stepInteraction: true,
      },
      {
        actionBefore: () => navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true),
        selector: '[data-tour="recommenderDisplayConfigurationTab"]',
        content: <p>Click on Display Configuration to view where and how the product blocks will be displayed.</p>,
      },
      {
        actionBefore: () => {
          document.querySelector('[data-tour="recommenderDisplayConfigurationTab"]')?.click();
          navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true);
        },
        selector: '[data-tour="templateField"]',
        content: (
          <>
            <p>The layout changes how the product block is visualized on your website.</p>
            <p>Changes might include font, color scheme, number of items per row according to the display size, etc.</p>
          </>
        ),
        stepInteraction: true,
      },
      {
        // The selector and strategy rows moved to the Appearance tab with the
        // rest of "where the block goes", so the step has to open that tab —
        // an anchor whose element is on another tab is simply not in the DOM,
        // and the step fails with nothing to point at.
        actionBefore: () => {
          document.querySelector('[data-tour="recommenderAppearanceTab"]')?.click();
          navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true);
        },
        selector: '[data-tour="cssSelectorField"]',
        content: (
          <>
            <p>
              This field allows you to attach the product block to an element on your website directly from the front
              end of your website.
            </p>
            <p>If you don't know how to get the CSS selector you can refer to the steps in this video.</p>
          </>
        ),
        stepInteraction: true,
      },
      {
        actionBefore: () => {
          document.querySelector('[data-tour="recommenderAppearanceTab"]')?.click();
          navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true);
        },
        selector: '[data-tour="strategyField"]',
        content: (
          <>
            <p>This setting determines how the product block will relate to the element it is attached to.</p>
            <p>
              Please note that the product block will appropriate the size of the element you attach it to. You can
              experiment with several selectors to find which one you like best.
            </p>
          </>
        ),
        stepInteraction: true,
      },
      {
        actionBefore: () => navigate(`${baseUrl}/pages/${recommender.pageId}/recommenders/new`, true),
        selector: '[data-tour="urlFilterField"]',
        content: (
          <>
            <p>
              You may choose to include or exclude this product block from specific pages depending on your sales
              strategy.
            </p>
            <p>For example, you want product blocks with a discounted products to show only on a particular page.</p>
          </>
        ),
        stepInteraction: true,
      },
      {
        actionBefore: () => navigate(`${baseUrl}/guide`, true),
        selector: '[data-tour="userGuideHeading"]',
        content: createFinishStepContent(),
      },
    ],
    isComplete: !!user.userActions.find((a) => a.action === 'taskCompleted' && a.context.task === 'createRecommender'),
  };
};

export default createRecommender;
